/**
 * Enhanced Social Sharing Extension - RRSSB Inspired
 * 
 * Modern responsive social sharing with RRSSB patterns
 * Mobile-only Web Share API, side positioning
 * Ridiculously responsive design with WordPress integration
 * 
 * @package LVBL Excellence Companion
 * @since 1.0.0
 */

// Import RRSSB-inspired styles
import './social-sharing.scss';

// Use WordPress globals instead of imports for better compatibility
const { addAction, addFilter, applyFilters, doAction } = wp.hooks;
const domReady = wp.domReady;

/**
 * Modern Responsive Social Sharing Buttons
 * Inspired by RRSSB with modern JavaScript patterns
 */
class LVBLSocialSharing {
    
    constructor() {
        // Configuration
        this.config = {
            selectors: {
                container: '.rrssb-buttons',
                button: 'li',
                text: '.rrssb-text',
                icon: '.rrssb-icon'
            },
            breakpoints: {
                tiny: 200,      // All icon-only
                large: 640      // All with labels
            },
            buttonSizes: {
                iconOnly: 42,   // Fixed width for icon-only buttons
                minPadding: 55  // Min space needed for text (padding + icon)
            },
            debounceDelay: 200
        };
        
        // State management
        this.containers = new Map(); // Store container data
        this.resizeObserver = null;
        
        // Share data
        this.shareData = {
            url: this.getCurrentUrl(),
            title: this.getCurrentTitle(),
            text: this.getCurrentDescription()
        };
        
        // Device detection
        this.isMobile = this.isMobileDevice();
        this.webShareEnabled = this.isWebShareSupported() && this.isMobile;
        
        this.init();
    }
    
    /**
     * Initialize modern social sharing
     */
    init() {
        window.LVBL_DEBUG && console.log('🚀 Modern Responsive Social Sharing initialized');
        
        // Find all button containers
        const containers = document.querySelectorAll(this.config.selectors.container);
        
        if (containers.length === 0) {
            window.LVBL_DEBUG && console.log('ℹ️ No social sharing containers found');
            return;
        }
        
        // Initialize each container
        containers.forEach((container, index) => {
            this.initializeContainer(container, index);
        });
        
        // Setup resize observation
        this.setupResizeObserver();
        
        // Setup event handlers
        this.attachEventHandlers();
        
        // Setup WordPress hooks
        this.setupWordPressHooks();
        
        window.LVBL_DEBUG && console.log(`✅ Initialized ${containers.length} social sharing container(s)`);
    }
    
    /**
     * Initialize a single container
     */
    initializeContainer(container, index) {
        // Add unique identifier
        container.classList.add(`rrssb-instance-${index + 1}`);
        
        // Get all buttons
        const buttons = Array.from(container.querySelectorAll(this.config.selectors.button));
        
        // Measure and cache text widths
        const buttonData = buttons.map(button => {
            const textElement = button.querySelector(this.config.selectors.text);
            const textWidth = textElement ? this.measureTextWidth(textElement) : 0;
            
            return {
                element: button,
                textElement,
                textWidth,
                isSmall: false
            };
        });
        
        // Store container data
        this.containers.set(container, {
            element: container,
            buttons: buttonData,
            width: container.offsetWidth
        });
        
        // Apply initial layout
        this.updateContainerLayout(container);
    }
    
    /**
     * Measure text width accurately
     */
    measureTextWidth(textElement) {
        // Create temporary measurement element
        const temp = document.createElement('span');
        temp.style.cssText = `
            visibility: hidden;
            position: absolute;
            white-space: nowrap;
            font-family: ${window.getComputedStyle(textElement).fontFamily};
            font-size: ${window.getComputedStyle(textElement).fontSize};
            font-weight: ${window.getComputedStyle(textElement).fontWeight};
            text-transform: ${window.getComputedStyle(textElement).textTransform};
        `;
        temp.textContent = textElement.textContent;
        
        document.body.appendChild(temp);
        const width = temp.offsetWidth;
        document.body.removeChild(temp);
        
        return width;
    }
    
    /**
     * Update container layout based on available space
     */
    updateContainerLayout(container) {
        const data = this.containers.get(container);
        if (!data) return;
        
        const containerWidth = container.offsetWidth;
        data.width = containerWidth;
        
        // Determine layout state
        const state = this.determineLayoutState(containerWidth);
        
        // Apply state classes
        container.classList.remove('rrssb-compact', 'rrssb-balanced', 'rrssb-expanded');
        container.classList.add(`rrssb-${state}`);
        
        // Calculate and apply button widths
        this.applyButtonWidths(container, state);
    }
    
    /**
     * Determine layout state based on container width
     */
    determineLayoutState(width) {
        if (width < this.config.breakpoints.tiny) {
            return 'compact';  // All icon-only
        } else if (width >= this.config.breakpoints.large) {
            return 'expanded'; // All with labels
        } else {
            return 'balanced'; // Smart mix
        }
    }
    
    /**
     * Apply button widths based on state
     */
    applyButtonWidths(container, state) {
        const data = this.containers.get(container);
        if (!data) return;
        
        const { buttons, width: containerWidth } = data;
        const buttonCount = buttons.length;
        
        switch (state) {
            case 'compact':
                // All buttons icon-only, equal width
                this.applyCompactLayout(buttons, containerWidth, buttonCount);
                break;
                
            case 'expanded':
                // All buttons with text, equal width
                this.applyExpandedLayout(buttons, containerWidth, buttonCount);
                break;
                
            case 'balanced':
                // Smart mix: some icon-only, some with text
                this.applyBalancedLayout(buttons, containerWidth);
                break;
        }
    }
    
    /**
     * Apply compact layout (all icon-only)
     */
    applyCompactLayout(buttons, containerWidth, buttonCount) {
        const buttonWidth = containerWidth / buttonCount;
        
        buttons.forEach(({ element, textElement }) => {
            element.classList.add('small');
            element.style.width = `${buttonWidth}px`;
            if (textElement) {
                textElement.style.visibility = 'hidden';
            }
        });
    }
    
    /**
     * Apply expanded layout (all with text)
     */
    applyExpandedLayout(buttons, containerWidth, buttonCount) {
        const buttonWidth = containerWidth / buttonCount;
        
        buttons.forEach(({ element, textElement }) => {
            element.classList.remove('small');
            element.style.width = `${buttonWidth}px`;
            if (textElement) {
                textElement.style.visibility = 'visible';
            }
        });
    }
    
    /**
     * Apply balanced layout (smart mix)
     */
    applyBalancedLayout(buttons, containerWidth) {
        const iconOnlyWidth = this.config.buttonSizes.iconOnly;
        const minTextSpace = this.config.buttonSizes.minPadding;
        
        // Determine which buttons can show text
        let availableWidth = containerWidth;
        const buttonStates = buttons.map(({ textWidth }) => {
            const needsWidth = textWidth + minTextSpace;
            return { needsWidth, canShowText: false };
        });
        
        // Progressive enhancement: try to show as many text labels as possible
        let iconsOnlyCount = 0;
        
        // Start from the end (least important buttons) and work backwards
        for (let i = buttons.length - 1; i >= 0; i--) {
            const requiredSpace = buttonStates[i].needsWidth;
            const equalShare = (availableWidth - (iconsOnlyCount * iconOnlyWidth)) / (i + 1);
            
            if (equalShare >= requiredSpace) {
                // This button and all before it can show text
                for (let j = 0; j <= i; j++) {
                    buttonStates[j].canShowText = true;
                }
                break;
            } else {
                // This button must be icon-only
                iconsOnlyCount++;
            }
        }
        
        // Apply widths
        const textButtonCount = buttons.length - iconsOnlyCount;
        const remainingWidth = containerWidth - (iconsOnlyCount * iconOnlyWidth);
        const textButtonWidth = textButtonCount > 0 ? remainingWidth / textButtonCount : 0;
        
        buttons.forEach(({ element, textElement }, index) => {
            if (buttonStates[index].canShowText) {
                // Show text
                element.classList.remove('small');
                element.style.width = `${textButtonWidth}px`;
                if (textElement) {
                    textElement.style.visibility = 'visible';
                }
            } else {
                // Icon only
                element.classList.add('small');
                element.style.width = `${iconOnlyWidth}px`;
                if (textElement) {
                    textElement.style.visibility = 'hidden';
                }
            }
        });
    }
    
    /**
     * Setup ResizeObserver for efficient resize detection
     */
    setupResizeObserver() {
        // Use ResizeObserver for modern, efficient resize detection
        if ('ResizeObserver' in window) {
            this.resizeObserver = new ResizeObserver(entries => {
                for (const entry of entries) {
                    this.updateContainerLayout(entry.target);
                }
            });
            
            // Observe all containers
            this.containers.forEach((data, container) => {
                this.resizeObserver.observe(container);
            });
            
            window.LVBL_DEBUG && console.log('✅ Using ResizeObserver for resize detection');
        } else {
            // Fallback to window resize with debounce
            this.setupFallbackResizeHandler();
            window.LVBL_DEBUG && console.log('⚠️ ResizeObserver not supported, using fallback');
        }
    }
    
    /**
     * Fallback resize handler for older browsers
     */
    setupFallbackResizeHandler() {
        let resizeTimer;
        
        window.addEventListener('resize', () => {
            if (resizeTimer) {
                clearTimeout(resizeTimer);
            }
            
            resizeTimer = setTimeout(() => {
                this.containers.forEach((data, container) => {
                    this.updateContainerLayout(container);
                });
            }, this.config.debounceDelay);
        });
    }
    
    /**
     * Setup WordPress hooks integration
     */
    setupWordPressHooks() {
        if (typeof wp === 'undefined' || !wp.hooks) return;
        
        const { applyFilters, doAction } = wp.hooks;
        
        // Allow modification of share data
        this.shareData = applyFilters('lvbl.socialSharing.shareData', this.shareData);
        
        // Fire ready action
        doAction('lvbl.socialSharing.ready', this);
    }
    
    /**
     * Attach event handlers for sharing
     */
    attachEventHandlers() {
        // Use event delegation for clicks
        document.addEventListener('click', (e) => {
            const link = e.target.closest('.rrssb-buttons a[data-social-platform]');
            if (!link) return;
            
            e.preventDefault();
            e.stopPropagation();
            
            const platform = link.getAttribute('data-social-platform');
            this.handleShare(platform, link);
        });
    }
    
    /**
     * Handle share action
     */
    async handleShare(platform, link) {
        try {
            link.classList.add('sharing');
            
            // Handle special platforms
            switch (platform) {
                case 'webshare':
                    await this.handleWebShare(link);
                    break;
                case 'copy':
                    await this.handleCopyLink(link);
                    break;
                case 'print':
                    this.handlePrint();
                    break;
                default:
                    // Let default behavior handle it (popup or direct link)
                    if (link.classList.contains('popup')) {
                        this.openPopup(link.href, 580, 470);
                    }
                    break;
            }
            
            // Track the share
            this.trackShare(platform);
            
        } catch (error) {
            console.error('Share error:', error);
        } finally {
            setTimeout(() => {
                link.classList.remove('sharing');
            }, 1000);
        }
    }
    
    /**
     * Handle Web Share API
     */
    async handleWebShare(link) {
        if (!navigator.share) {
            console.warn('Web Share API not supported');
            return;
        }
        
        try {
            await navigator.share({
                title: this.shareData.title,
                text: this.shareData.text,
                url: this.shareData.url
            });
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error('Web Share failed:', error);
            }
        }
    }
    
    /**
     * Handle copy link
     */
    async handleCopyLink(link) {
        try {
            await navigator.clipboard.writeText(this.shareData.url);
            this.showNotification('Link copied to clipboard!', 'success');
        } catch (error) {
            // Fallback for older browsers
            this.fallbackCopyText(this.shareData.url);
        }
    }
    
    /**
     * Fallback copy method
     */
    fallbackCopyText(text) {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        
        try {
            document.execCommand('copy');
            this.showNotification('Link copied!', 'success');
        } catch (error) {
            console.error('Copy failed:', error);
        }
        
        document.body.removeChild(textarea);
    }
    
    /**
     * Handle print
     */
    handlePrint() {
        window.print();
    }
    
    /**
     * Open popup window
     */
    openPopup(url, width, height) {
        const left = (window.innerWidth - width) / 2 + window.screenLeft;
        const top = (window.innerHeight - height) / 2 + window.screenTop;
        
        window.open(
            url,
            'share',
            `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`
        );
    }
    
    /**
     * Show notification
     */
    showNotification(message, type = 'info') {
        // Simple console log for now - can be enhanced with toast notifications
        window.LVBL_DEBUG && console.log(`[${type.toUpperCase()}] ${message}`);
        
        // Could integrate with WordPress notices or custom toast system
    }
    
    /**
     * Track share event
     */
    trackShare(platform) {
        // Send tracking event if analytics is enabled
        if (typeof gtag === 'function') {
            gtag('event', 'share', {
                method: platform,
                content_type: 'article',
                item_id: this.shareData.url
            });
        }
        
        // WordPress hook for custom tracking
        if (typeof wp !== 'undefined' && wp.hooks) {
            wp.hooks.doAction('lvbl.socialSharing.shared', platform, this.shareData);
        }
    }
    
    /**
     * Show fallback share options when Web Share fails
     */
    showShareFallback(shareData) {
        window.LVBL_DEBUG && console.log('Showing fallback share options');
        // Could implement a custom share dialog here
    }
    
    /**
     * Handle copy link functionality
     */
    async handleCopyLink(url, button) {
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(url);
            } else {
                this.fallbackCopyToClipboard(url);
            }
            
            this.showFeedback(button, 'success', 'Link copied!');
            
        } catch (error) {
            console.error('Copy failed:', error);
            this.showFeedback(button, 'error', 'Copy failed. Please try manually.');
        }
    }
    
    /**
     * Fallback copy to clipboard for older browsers
     */
    fallbackCopyToClipboard(text) {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
            document.execCommand('copy');
        } finally {
            document.body.removeChild(textArea);
        }
    }
    
    /**
     * Handle print functionality
     */
    handlePrint(button) {
        try {
            window.print();
            this.showFeedback(button, 'success', 'Print dialog opened');
        } catch (error) {
            console.error('Print failed:', error);
            this.showFeedback(button, 'error', 'Print failed');
        }
    }
    
    /**
     * Handle traditional platform sharing
     */
    handleTraditionalShare(platform, shareData, button) {
        const platformConfig = this.settings.platforms[platform];
        if (!platformConfig.url) return;
        
        // Build share URL
        let shareUrl = platformConfig.url
            .replace('{url}', encodeURIComponent(shareData.url))
            .replace('{title}', encodeURIComponent(shareData.title))
            .replace('{text}', encodeURIComponent(shareData.text));
        
        if (platformConfig.popup && !this.settings.isMobile) {
            this.openPopup(shareUrl, `Share on ${platformConfig.name}`, button);
        } else {
            window.open(shareUrl, '_blank', 'noopener,noreferrer');
        }
        
        this.showFeedback(button, 'success', `Opening ${platformConfig.name}...`);
    }
    
    /**
     * Open popup window for sharing
     */
    openPopup(url, title, button) {
        const width = 580;
        const height = 470;
        const left = (window.innerWidth - width) / 2;
        const top = (window.innerHeight - height) / 2;
        
        const popup = window.open(
            url,
            title,
            `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
        );
        
        if (popup && popup.focus) {
            popup.focus();
        }
    }
    
    /**
     * Show user feedback
     */
    showFeedback(button, type, message) {
        // Create or update feedback element
        let feedback = button.querySelector('.share-feedback');
        if (!feedback) {
            feedback = document.createElement('span');
            feedback.className = 'share-feedback';
            button.appendChild(feedback);
        }
        
        feedback.textContent = message;
        feedback.className = `share-feedback ${type}`;
        
        // Auto-hide feedback
        setTimeout(() => {
            if (feedback.parentNode) {
                feedback.remove();
            }
        }, 3000);
    }
    
    /**
     * Track sharing analytics
     */
    trackShare(platform, shareData) {
        // Google Analytics 4
        if (typeof gtag !== 'undefined') {
            gtag('event', 'share', {
                method: platform,
                content_type: 'article',
                item_id: shareData.url
            });
        }
        
        // Universal Analytics fallback
        if (typeof ga !== 'undefined') {
            ga('send', 'event', 'Social', 'Share', platform);
        }
        
        // WordPress hook for custom tracking
        doAction('lvbl.socialSharing.tracked', platform, shareData);
        
        // Update share count
        this.updateShareCount(platform);
    }
    
    /**
     * Device detection
     */
    isMobileDevice() {
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
               window.innerWidth <= 768;
    }
    
    /**
     * Web Share API detection
     */
    isWebShareSupported() {
        return 'share' in navigator;
    }
    
    /**
     * Get current page URL
     */
    getCurrentUrl() {
        return window.location.href;
    }
    
    /**
     * Get current page title
     */
    getCurrentTitle() {
        return document.title || '';
    }
    
    /**
     * Get current page description
     */
    getCurrentDescription() {
        const metaDescription = document.querySelector('meta[name="description"]');
        return metaDescription ? metaDescription.content : document.title;
    }
    
    /**
     * Cleanup on destroy
     */
    destroy() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
        this.containers.clear();
    }
}

/**
 * Initialize when DOM is ready
 */
if (typeof wp !== 'undefined' && wp.domReady) {
    wp.domReady(() => {
        window.LVBL_DEBUG && console.log('🔍 DOM ready, checking for social sharing elements...');
        
        const containers = document.querySelectorAll('.rrssb-buttons');
        
        if (containers.length > 0) {
            window.LVBL_DEBUG && console.log(`✅ Found ${containers.length} social sharing container(s), initializing...`);
            window.lvblSocialSharing = new LVBLSocialSharing();
            
            // Fire WordPress action
            if (wp.hooks) {
                wp.hooks.doAction('lvbl.socialSharing.initialized', window.lvblSocialSharing);
            }
        } else {
            window.LVBL_DEBUG && console.log('ℹ️ No social sharing elements found on this page');
        }
    });
} else {
    // Fallback for non-WordPress environments
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            const containers = document.querySelectorAll('.rrssb-buttons');
            if (containers.length > 0) {
                window.lvblSocialSharing = new LVBLSocialSharing();
            }
        });
    } else {
        const containers = document.querySelectorAll('.rrssb-buttons');
        if (containers.length > 0) {
            window.lvblSocialSharing = new LVBLSocialSharing();
        }
    }
}

// Export for module systems
export default LVBLSocialSharing;
